import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <article className="group cursor-pointer">
        <Link to={`/article/${article.id}`} className="block">
          <div className="relative aspect-[16/10] overflow-hidden rounded-lg mb-4">
            <div className="w-full h-full">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                title={`${article.title} - 淫母日记`}
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-background-50/90 via-background-50/20 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <span className="inline-block px-2.5 py-1 rounded-full text-xs font-label bg-primary-500/80 text-background-50 mb-3">
                {article.category}
              </span>
              <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground-50 leading-snug line-clamp-2">
                {article.title}
              </h3>
              <p className="mt-2 text-sm text-foreground-400 line-clamp-2 leading-relaxed">
                {article.excerpt}
              </p>
              <div className="mt-3 flex items-center gap-3 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.readCount > 10000
                    ? `${(article.readCount / 10000).toFixed(1)}万`
                    : article.readCount}
                </span>
              </div>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  if (variant === 'compact') {
    return (
      <article className="group">
        <Link to={`/article/${article.id}`} className="flex gap-4 cursor-pointer">
          <div className="w-24 h-24 md:w-28 md:h-28 flex-shrink-0 overflow-hidden rounded-lg">
            <div className="w-full h-full">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                title={`${article.title} - 淫母日记`}
              />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-xs font-label text-primary-400">{article.category}</span>
            <h4 className="mt-1 text-sm md:text-base font-heading font-medium text-foreground-200 line-clamp-2 leading-snug group-hover:text-foreground-50 transition-colors">
              {article.title}
            </h4>
            <p className="mt-1 text-xs text-foreground-500 line-clamp-2 leading-relaxed">
              {article.excerpt}
            </p>
            <div className="mt-2 flex items-center gap-3 text-xs text-foreground-600">
              <span>{article.date}</span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line text-[10px]"></i>
                {article.readCount > 10000
                  ? `${(article.readCount / 10000).toFixed(1)}万`
                  : article.readCount}
              </span>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group h-full">
      <Link to={`/article/${article.id}`} className="block h-full cursor-pointer">
        <div className="h-full bg-background-100 rounded-lg overflow-hidden transition-all duration-300 hover:bg-background-200/70">
          <div className="aspect-[16/10] overflow-hidden">
            <div className="w-full h-full">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                title={`${article.title} - 淫母日记`}
              />
            </div>
          </div>
          <div className="p-4 md:p-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-label text-primary-400">{article.category}</span>
              {article.featured && (
                <span className="text-[10px] font-label px-1.5 py-0.5 rounded bg-accent-500/20 text-accent-400">
                  推荐
                </span>
              )}
            </div>
            <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-100 line-clamp-2 leading-snug group-hover:text-foreground-50 transition-colors">
              {article.title}
            </h3>
            <p className="mt-2 text-sm text-foreground-500 line-clamp-2 leading-relaxed">
              {article.excerpt}
            </p>
            <div className="mt-3 pt-3 border-t border-background-200/40 flex items-center justify-between text-xs text-foreground-600">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.readCount > 10000
                  ? `${(article.readCount / 10000).toFixed(1)}万`
                  : article.readCount}
              </span>
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
}